/** @jsx isolateComponent */

import { css } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import {
  Colors,
  Link,
  Shadows,
  Typography,
  useTheme,
} from '@zapier/design-system';

import type { UniversalNavLinkType } from '../../../constants';

type Props = {
  /**
   * Array of links to render within dropdown menu.
   */
  links: Array<UniversalNavLinkType>;
};

const Overrides = {
  root: css`
    background-color: ${Colors.neutral100};
    border: 1px solid ${Colors.neutral400};
    border-radius: 3px;
  `,
  link: css`
    border-radius: 3px;
    color: ${Colors.neutral800};
    font-size: 18px;
    font-weight: 600;
    line-height: 25px;
    &:hover,
    &:focus {
      color: ${Colors.neutral800};
      background-color: ${Colors.cream};
      outline: none;
    }
  `,
  linkTitle: (hasDescription: boolean) => css`
    font-weight: ${hasDescription ? 600 : 500};
  `,
  linkDescription: css`
    color: ${Colors.neutral800};
    font-size: 16px;
    line-height: 22px;
    font-weight: 400;
  `,
};

const Styles = labelStyles('Submenu', {
  root: (isRebrand: boolean) => css`
    background-color: ${Colors.white};
    border-radius: 0 0 10px 10px;
    padding: 20px;
    box-shadow: ${Shadows.elevation20};
    ${isRebrand && Overrides.root};
  `,

  list: css`
    display: grid;
    grid-template-columns: 100%;
    gap: 5px;
  `,

  link: (isRebrand: boolean) => css`
    ${Typography.base};
    display: block;
    border-radius: 10px;
    color: ${Colors.gray};
    line-height: 20px;
    font-size: 14px;
    padding: 10px;
    text-decoration: none;

    &:hover,
    &:focus {
      color: ${Colors.ocean};
      background-color: ${Colors.ocean5};
      outline: none;
    }
    ${isRebrand && Overrides.link};
  `,

  linkInner: css`
    transform: translateY(1px);
  `,

  linkTitle: (isRebrand: boolean, hasDescription: boolean) => css`
    display: block;
    font-weight: 700;
    ${isRebrand && Overrides.linkTitle(hasDescription)};
  `,

  linkDescription: (isRebrand: boolean) => css`
    display: block;
    margin-top: 5px;
    color: ${Colors.gray80};
    font-size: 12px;
    line-height: 15px;
    ${isRebrand && Overrides.linkDescription};
  `,
});

const SubmenuLink = (props: UniversalNavLinkType) => {
  const { isRebrand } = useTheme();
  const hasDescription = !!props.description;
  return (
    <Link
      color={null}
      css={Styles.link(isRebrand)}
      href={props.href || undefined}
      role="menuitem"
    >
      <div css={Styles.linkInner}>
        <p css={Styles.linkTitle(isRebrand, hasDescription)}>{props.text}</p>
        {props.description && (
          <p css={Styles.linkDescription(isRebrand)}>{props.description}</p>
        )}
      </div>
    </Link>
  );
};

/**
 * @private
 * Submenu component acts as a dropdown on universal nav links
 */
const Submenu = (props: Props) => {
  const { isRebrand } = useTheme();
  const linkItems = props.links.map((link) => {
    // We set role="none" here in combination with role="menuitem" above on
    // the Link element for accessibility, given role="menu" in the ul below.
    // https://www.w3.org/TR/wai-aria-practices/examples/menu-button/menu-button-links.html
    return (
      <li key={link.text} role="none">
        <SubmenuLink {...link} />
      </li>
    );
  });
  return (
    <div css={Styles.root(isRebrand)}>
      <ul css={Styles.list} role="menu">
        {linkItems}
      </ul>
    </div>
  );
};

export default Submenu;
